import { useCallback, useEffect, useRef, useState } from "react";

// ---- Acaia (Pearl / Lunar / Pyxis) BLE protocol ----
// Verified against tatemazer/AcaiaArduinoBLE + artisan acaia.py.
const ACAIA_NEW_SERVICE = "49535343-fe7d-4ae5-8fa9-9fafd205e455";
const ACAIA_NEW_READ = "49535343-1e4d-4bd9-ba61-23c647249616";
const ACAIA_NEW_WRITE = "49535343-8841-43f4-a8d4-ecbe34729bb3";
const ACAIA_OLD_SERVICE = "00001820-0000-1000-8000-00805f9b34fb";
const ACAIA_OLD_CHAR = "00002a80-0000-1000-8000-00805f9b34fb";

const IDENTIFY = [0xef, 0xdd, 0x0b, 0x30, 0x31, 0x32, 0x33, 0x34, 0x35, 0x36, 0x37, 0x38, 0x39, 0x30, 0x31, 0x32, 0x33, 0x34, 0x9a, 0x6d];
const NOTIFICATION_REQUEST = [0xef, 0xdd, 0x0c, 0x09, 0x00, 0x01, 0x01, 0x02, 0x02, 0x05, 0x03, 0x04, 0x15, 0x06];
const HEARTBEAT = [0xef, 0xdd, 0x00, 0x02, 0x00, 0x02, 0x00];
const TARE_ACAIA = [0xef, 0xdd, 0x04, 0x00, 0x00, 0x00];
const HEARTBEAT_MS = 1000;      // keepalive cadence — must stay well under the Acaia idle timeout

// Every service we might touch must be declared for Web Bluetooth.
// All full 128-bit strings — iOS Bluetooth browsers (Bluefy) choke on raw 16-bit numbers.
const COMMON_SERVICES = [
  ACAIA_NEW_SERVICE, ACAIA_OLD_SERVICE,
  "0000ffe0-0000-1000-8000-00805f9b34fb",
  "0000fff0-0000-1000-8000-00805f9b34fb",
  "0000181d-0000-1000-8000-00805f9b34fb",
  "6e400001-b5a3-f393-e0a9-e50e24dcca9e", // Nordic UART
];

// Name prefixes of supported scales. Name-only filters are the most widely
// supported form (desktop Chrome AND Bluefy/WebBLE on iOS).
const SCALE_NAME_PREFIXES = ["PEARL", "LUNAR", "PYXIS", "ACAIA", "PROCH", "CINCO", "BOOKO", "FELIC"];
const SCALE_FILTERS = SCALE_NAME_PREFIXES.map((namePrefix) => ({ namePrefix }));

const OZ_TO_G = 28.3495;

function toHex(dv) {
  const b = new Uint8Array(dv.buffer || dv);
  return Array.from(b).map((x) => x.toString(16).padStart(2, "0")).join(" ");
}

const sleep = (ms) => new Promise((r) => setTimeout(r, ms));

// Acaia weight (grams) from a notification packet; null if not a weight packet.
function decodeAcaia(dv, type) {
  const b = new Uint8Array(dv.buffer);
  const l = b.length;
  if (type === "new" && (l === 13 || l === 17) && b[4] === 0x05) {
    return (((b[6] & 0xff) << 8) + (b[5] & 0xff)) / Math.pow(10, b[9]) * ((b[10] & 0x02) ? -1 : 1);
  }
  if (type === "old" && (l === 10 || l === 14)) {
    return (((b[3] & 0xff) << 8) + (b[2] & 0xff)) / Math.pow(10, b[6]) * ((b[7] & 0x02) ? -1 : 1);
  }
  return null;
}

// Generic fallback for simple notify-only scales → grams.
function decodeGeneric(dv, uuid) {
  const id = String(uuid).toLowerCase();
  if (id.includes("2a9d")) {
    const flags = dv.getUint8(0);
    const raw = dv.getUint16(1, true);
    return flags & 0x01 ? raw * 0.01 * 453.592 : raw * 0.005 * 1000;
  }
  if (dv.byteLength >= 2) return dv.getInt16(dv.byteLength - 2, true) / 10;
  return 0;
}

export function useBleScale() {
  const supported = typeof navigator !== "undefined" && !!navigator.bluetooth;
  const [status, setStatus] = useState("idle"); // idle | connecting | connected | error
  const [deviceName, setDeviceName] = useState("");
  const [protocol, setProtocol] = useState(""); // acaia | generic
  const [raw, setRaw] = useState(0);
  const [stable, setStable] = useState(true);
  const [error, setError] = useState("");
  const [offset, setOffset] = useState(0);
  const [debug, setDebug] = useState([]); // recent raw frames for calibration
  const [knownName, setKnownName] = useState(""); // remembered scale for one-tap reconnect
  const [frameCount, setFrameCount] = useState(0); // notifications received since connect
  const [dropCount, setDropCount] = useState(0);   // unexpected disconnects since connect
  const [writeStatus, setWriteStatus] = useState(""); // "ok" | "ok~" | "ERR ..."
  const [pathInfo, setPathInfo] = useState("");    // which protocol path + service UUIDs

  const deviceRef = useRef(null);
  const knownDeviceRef = useRef(null);
  const charsRef = useRef([]);
  const writeRef = useRef(null);
  const hbRef = useRef(null);
  const offsetRef = useRef(0);
  const samplesRef = useRef([]);
  const stableTimer = useRef(null);
  const userDisconnectRef = useRef(false);  // true => we closed it on purpose
  const runSetupRef = useRef(null);         // latest runSetup, for use inside the drop handler
  const writeQueueRef = useRef(Promise.resolve()); // FIFO queue — Web Bluetooth rejects concurrent writes
  const lastPacketRef = useRef(0);          // timestamp of the last weight packet (liveness)
  const recoverAttemptsRef = useRef(0);     // consecutive auto-reconnect attempts (backoff)
  const recoverTimerRef = useRef(null);

  const weight = Math.round((raw - offset) * 10) / 10;
  const hasHardwareTare = protocol === "acaia";

  const ingest = useCallback((g) => {
    if (!Number.isFinite(g)) return;
    lastPacketRef.current = Date.now();   // link is alive
    recoverAttemptsRef.current = 0;       // healthy again → reset backoff
    setRaw(g);
    const now = Date.now();
    samplesRef.current.push({ g, now });
    samplesRef.current = samplesRef.current.filter((s) => now - s.now < 900);
    const vals = samplesRef.current.map((s) => s.g);
    setStable(Math.max(...vals) - Math.min(...vals) < 0.6);
    if (stableTimer.current) clearTimeout(stableTimer.current);
    stableTimer.current = setTimeout(() => setStable(true), 900);
  }, []);

  const pushDebug = useCallback((dv, decoded) => {
    setFrameCount((n) => n + 1);
    setDebug((d) => [{ t: Date.now(), hex: toHex(dv), len: dv.byteLength, val: decoded }, ...d].slice(0, 6));
  }, []);

  const enqueueWrite = useCallback((ch, arr) => {
    // Serialize writes (Web Bluetooth rejects concurrent ones) BUT never let a write
    // that never resolves — an iOS/Bluefy quirk — block the queue, or the heartbeat
    // would stall and the scale would time out. So each write races a short timeout.
    const data = Uint8Array.from(arr);
    const timedWrite = (fn, label) => Promise.race([
      fn().then(() => label).catch((e) => "ERR " + (e?.name || e?.message || "fail")),
      new Promise((res) => setTimeout(() => res(label), 700)), // assume fire-and-forget delivered
    ]);
    const run = async () => {
      let result;
      // Acaia communicates over write-WITHOUT-response (fire-and-forget); prefer it.
      if (ch.writeValueWithoutResponse) {
        result = await timedWrite(() => ch.writeValueWithoutResponse(data), "ok");
        if (result.startsWith("ERR") && ch.properties?.write) {
          result = await timedWrite(() => ch.writeValue(data), "ok*");
        }
      } else {
        result = await timedWrite(() => ch.writeValue(data), "ok*");
      }
      setWriteStatus(result);
    };
    writeQueueRef.current = writeQueueRef.current.then(run, run);
    return writeQueueRef.current;
  }, []);

  const stopHeartbeat = () => { if (hbRef.current) { clearInterval(hbRef.current); hbRef.current = null; } };

  // Acaia often fails the first GATT connect — retry a few times.
  const connectGatt = async (device) => {
    let lastErr;
    for (let i = 0; i < 3; i++) {
      try { return await device.gatt.connect(); }
      catch (e) { lastErr = e; await new Promise((r) => setTimeout(r, 400)); }
    }
    throw lastErr;
  };

  const describeError = (err) => {
    const inFrame = typeof window !== "undefined" && window.self !== window.top;
    if (err != null && typeof err !== "object") {
      return `Bluefy couldn't start scanning (code ${String(err)}). The scale is usually still held by a stuck connection: fully quit Bluefy (swipe it away in the app switcher), turn the Pearl OFF then ON, make sure no phone/other app is paired to it, then reopen Bluefy and tap Connect.`;
    }
    if (err?.name === "SecurityError" || inFrame) {
      return "Open the Color Bar in its OWN browser tab — Bluetooth is blocked inside an embedded preview window.";
    }
    if (err?.name === "NotSupportedError" || err?.name === "TypeError") {
      return `This browser's Bluetooth is limited (${err?.name}). In Bluefy, make sure it's updated; otherwise use Chrome/Edge on a laptop.`;
    }
    if (err?.name === "NetworkError" || err?.name === "InvalidStateError") {
      return "Couldn't reach the scale. Wake it (set a bowl on it) and close the Acaia phone app so nothing else is connected, then try again.";
    }
    const raw = err ? (err.name ? `${err.name}: ${err.message || ""}` : String(err)) : "no error object";
    return `Couldn't connect — details: ${raw}. Wake the scale, close the Acaia app, then retry.`;
  };

  const disconnect = useCallback(() => {
    userDisconnectRef.current = true;
    if (recoverTimerRef.current) { clearTimeout(recoverTimerRef.current); recoverTimerRef.current = null; }
    recoverAttemptsRef.current = 0;
    lastPacketRef.current = 0;
    stopHeartbeat();
    try {
      charsRef.current.forEach((c) => { try { c.stopNotifications(); } catch { /* */ } });
      if (deviceRef.current?.gatt?.connected) deviceRef.current.gatt.disconnect();
    } catch { /* */ }
    charsRef.current = []; writeRef.current = null; deviceRef.current = null;
    setStatus("idle"); setDeviceName(""); setProtocol(""); setRaw(0); setStable(true);
    offsetRef.current = 0; setOffset(0); samplesRef.current = []; setDebug([]);
  }, []);

  // Reconnect to the SAME scale after an unexpected drop, with exponential backoff.
  const scheduleReconnect = useCallback((dev) => {
    if (userDisconnectRef.current || !dev) { setStatus("idle"); setDeviceName(""); return; }
    const attempt = recoverAttemptsRef.current;
    if (attempt >= 6) {
      recoverAttemptsRef.current = 0;
      setStatus("error");
      setError("The scale keeps dropping out. Keep the Pearl within a few feet of the iPad, make sure the Acaia phone app is fully closed, then tap Reconnect.");
      return;
    }
    recoverAttemptsRef.current = attempt + 1;
    const delay = Math.min(700 * Math.pow(2, attempt), 8000);
    setStatus("connecting");
    if (recoverTimerRef.current) clearTimeout(recoverTimerRef.current);
    recoverTimerRef.current = setTimeout(() => {
      if (userDisconnectRef.current || !runSetupRef.current) return;
      try { if (dev.gatt?.connected) dev.gatt.disconnect(); } catch { /* */ }
      setTimeout(() => {
        if (userDisconnectRef.current || !runSetupRef.current) return;
        runSetupRef.current(dev).catch(() => scheduleReconnect(dev));
      }, 300);
    }, delay);
  }, []);

  const onDisconnected = useCallback(() => {
    stopHeartbeat(); charsRef.current = []; writeRef.current = null;
    setProtocol(""); setRaw(0); setStable(true);
    setDropCount((n) => n + 1);
    scheduleReconnect(deviceRef.current);
  }, [scheduleReconnect]);

  // Shared setup once we hold a device (from the chooser or a remembered one).
  const runSetup = useCallback(async (device) => {
    deviceRef.current = device;
    setDeviceName(device.name || knownName || "Scale");
    device.removeEventListener("gattserverdisconnected", onDisconnected);
    device.addEventListener("gattserverdisconnected", onDisconnected);
    const server = await connectGatt(device);

    // Prefer an EXPLICIT service fetch — Bluefy/iOS often returns an incomplete list
    // from getPrimaryServices(), which wrongly drops a real Acaia scale into the
    // generic (no-handshake, no-heartbeat) path and makes it disconnect.
    let newSvc = null, oldSvc = null, services = [];
    try { newSvc = await server.getPrimaryService(ACAIA_NEW_SERVICE); } catch { /* not this gen */ }
    if (!newSvc) { try { oldSvc = await server.getPrimaryService(ACAIA_OLD_SERVICE); } catch { /* not this gen */ } }
    if (!newSvc && !oldSvc) {
      try { services = await server.getPrimaryServices(); } catch { /* */ }
      newSvc = services.find((s) => s.uuid === ACAIA_NEW_SERVICE) || null;
      oldSvc = services.find((s) => s.uuid === ACAIA_OLD_SERVICE) || null;
    }
    setPathInfo(newSvc ? "acaia-new" : oldSvc ? "acaia-old" : ("generic [" + (services.map((s) => s.uuid.slice(0, 8)).join(",") || "none") + "]"));

    // ---- Acaia path ----
    if (newSvc || oldSvc) {
      const type = newSvc ? "new" : "old";
      const readCh = newSvc ? await newSvc.getCharacteristic(ACAIA_NEW_READ) : await oldSvc.getCharacteristic(ACAIA_OLD_CHAR);
      const writeCh = newSvc ? await newSvc.getCharacteristic(ACAIA_NEW_WRITE) : readCh;
      await sleep(300);
      await readCh.startNotifications();
      readCh.addEventListener("characteristicvaluechanged", (e) => {
        const dv = e.target.value;
        const g = decodeAcaia(dv, type);
        pushDebug(dv, g);
        if (g != null) ingest(g);
      });
      charsRef.current = [readCh];
      writeRef.current = writeCh;
      await sleep(300);                          // let Bluefy's BLE bridge settle before writing
      await enqueueWrite(writeCh, IDENTIFY);
      await enqueueWrite(writeCh, NOTIFICATION_REQUEST);
      lastPacketRef.current = Date.now();
      const beat = () => { enqueueWrite(writeCh, HEARTBEAT); };
      beat();                                     // first keepalive immediately
      hbRef.current = setInterval(beat, HEARTBEAT_MS);
      // Re-arm the weight stream once more — the first handshake can be lost while
      // the connection is still settling, which leaves the scale silent.
      setTimeout(() => {
        if (hbRef.current) { enqueueWrite(writeCh, IDENTIFY); enqueueWrite(writeCh, NOTIFICATION_REQUEST); }
      }, 1300);
      setProtocol("acaia");
    } else {
      // ---- Generic path ----
      const notifiers = [];
      for (const svc of services) {
        let chars = [];
        try { chars = await svc.getCharacteristics(); } catch { continue; }
        for (const ch of chars) {
          if (ch.properties.notify || ch.properties.indicate) {
            try {
              await ch.startNotifications();
              ch.addEventListener("characteristicvaluechanged", (e) => {
                const dv = e.target.value;
                let g; try { g = decodeGeneric(dv, ch.uuid); } catch { return; }
                pushDebug(dv, g);
                ingest(g);
              });
              notifiers.push(ch);
            } catch { /* */ }
          }
        }
      }
      if (!notifiers.length) {
        disconnect();
        setError("Connected, but this scale doesn't stream weight. Weigh manually, or try a supported scale.");
        setStatus("error");
        return;
      }
      charsRef.current = notifiers;
      setProtocol("generic");
    }

    // Remember this scale for one-tap reconnect next time.
    knownDeviceRef.current = device;
    const nm = device.name || knownName || "Scale";
    setKnownName(nm);
    try { localStorage.setItem("shf_scale_name", nm); } catch { /* */ }
    setStatus("connected");
  }, [ingest, pushDebug, disconnect, onDisconnected, knownName, enqueueWrite]);

  useEffect(() => { runSetupRef.current = runSetup; }, [runSetup]);

  const connect = useCallback(async () => {
    if (!supported) { setError("This browser can't talk to Bluetooth — use Chrome or Edge on a laptop/desktop (or Bluefy on iPad)."); return; }
    userDisconnectRef.current = false; recoverAttemptsRef.current = 0;
    if (recoverTimerRef.current) { clearTimeout(recoverTimerRef.current); recoverTimerRef.current = null; }
    setFrameCount(0); setDropCount(0); setWriteStatus("");
    setError(""); setStatus("connecting"); setDebug([]);
    try {
      // requestDevice() MUST be the first await after the tap — any await before it
      // (getDevices/sleep) consumes the user gesture and Bluefy rejects with "code 2".
      let device;
      try {
        device = await navigator.bluetooth.requestDevice({ filters: SCALE_FILTERS, optionalServices: COMMON_SERVICES });
      } catch (e) {
        if (e?.name === "NotFoundError") throw e; // user cancelled / nothing matched
        // Fallback for browsers that dislike our filter set.
        device = await navigator.bluetooth.requestDevice({ acceptAllDevices: true, optionalServices: COMMON_SERVICES });
      }
      await runSetup(device);
    } catch (err) {
      if (err?.name === "NotFoundError") { setStatus("idle"); return; } // user cancelled chooser
      setError(describeError(err));
      setStatus("error");
    }
  }, [supported, runSetup]);

  // One-tap reconnect to the previously paired scale (no chooser).
  const reconnect = useCallback(async () => {
    if (!supported) return;
    userDisconnectRef.current = false; recoverAttemptsRef.current = 0;
    if (recoverTimerRef.current) { clearTimeout(recoverTimerRef.current); recoverTimerRef.current = null; }
    setFrameCount(0); setDropCount(0); setWriteStatus("");
    setError(""); setStatus("connecting"); setDebug([]);
    try {
      let device = knownDeviceRef.current;
      if (!device && navigator.bluetooth.getDevices) {
        const list = await navigator.bluetooth.getDevices();
        device = list && list[0];
      }
      if (!device) {
        setStatus("idle");
        setError('No remembered scale found. Tap "Connect scale" to pick your Pearl from the list.');
        return;
      }
      await runSetup(device);
    } catch (err) {
      if (err?.name === "NotFoundError") { setStatus("idle"); return; }
      setError(describeError(err));
      setStatus("error");
    }
  }, [supported, runSetup]);

  // Tare: Acaia gets a real hardware tare; others use a software zero offset.
  const zero = useCallback(() => {
    if (protocol === "acaia" && writeRef.current) {
      enqueueWrite(writeRef.current, TARE_ACAIA);
      offsetRef.current = 0; setOffset(0);
    } else {
      offsetRef.current = raw; setOffset(raw);
    }
  }, [protocol, raw, enqueueWrite]);

  useEffect(() => () => { stopHeartbeat(); if (stableTimer.current) clearTimeout(stableTimer.current); if (recoverTimerRef.current) clearTimeout(recoverTimerRef.current); }, []);

  // Load the previously paired scale so we can offer a one-tap reconnect.
  useEffect(() => {
    if (!supported) return;
    let cancelled = false;
    (async () => {
      try {
        const saved = typeof localStorage !== "undefined" ? localStorage.getItem("shf_scale_name") : "";
        if (saved && !cancelled) setKnownName(saved);
        if (navigator.bluetooth?.getDevices) {
          const list = await navigator.bluetooth.getDevices();
          if (!cancelled && list && list[0]) {
            knownDeviceRef.current = list[0];
            setKnownName(list[0].name || saved || "Scale");
          }
        }
      } catch { /* */ }
    })();
    return () => { cancelled = true; };
  }, [supported]);

  return {
    supported, status, deviceName, protocol, error, debug, knownName,
    weight, raw: Math.round(raw * 10) / 10, stable, hasHardwareTare,
    frameCount, dropCount, writeStatus, pathInfo,
    connected: status === "connected",
    connecting: status === "connecting",
    connect, reconnect, disconnect, zero,
    OZ_TO_G,
  };
}
